import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { Provider } from 'react-redux';
import { store } from './store';
import theme from './styles/theme';

// Layout Components
import Header from './components/Layout/Header';
import Footer from './components/Layout/Footer';

// Page Components
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import JoinPage from './pages/JoinPage';
import RegistrationQRPage from './pages/RegistrationQRPage';
import DashboardPage from './pages/DashboardPage';
import ProfilePage from './pages/ProfilePage';
import EditProfilePage from './pages/EditProfilePage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import CheckInManagementPage from './pages/CheckInManagementPage';
import KidsClubBookingPage from './pages/KidsClubBookingPage';
import KidsClubStaffPage from './pages/KidsClubStaffPage';
import KidsClubAdminPage from './pages/KidsClubAdminPage';
import NonMemberManagementPage from './pages/NonMemberManagementPage';
import NonMemberDetailPage from './pages/NonMemberDetailPage';
import MemberDetailPage from './pages/MemberDetailPage';
import ConversionApplicationPage from './pages/ConversionApplicationPage';
import CourtBookingPage from './pages/CourtBookingPage';
import ProManagementPage from './pages/ProManagementPage';
import SwimClassesPage from './pages/SwimClassesPage';
import MyEnrollmentsPage from './pages/MyEnrollmentsPage';
import PendingEnrollmentsAdminPage from './pages/PendingEnrollmentsAdminPage';
import ProgramCancellationsPage from './pages/ProgramCancellationsPage';
import ClassLevelsAdminPage from './pages/ClassLevelsAdminPage';
import SwimLeaguesAdminPage from './pages/SwimLeaguesAdminPage';
import CoachRosterPage from './pages/CoachRosterPage';
import AdminProgramEnrollmentsPage from './pages/AdminProgramEnrollmentsPage';
import SwimProgramsAdminPage from './pages/SwimProgramsAdminPage';
import MyScheduleParentPage from './pages/MyScheduleParentPage';
import CoachSchedulePage from './pages/CoachSchedulePage';
import FeatureFlagsAdminPage from './pages/FeatureFlagsAdminPage';

// Protected Route Component
import ProtectedRoute from './components/ProtectedRoute';
import { FeaturesProvider, useFeatureFlags, FeatureKey } from './features';
import { SnackbarProvider } from './components/common/SnackbarProvider';

/** Renders the child only when the feature flag is on; otherwise redirects to /dashboard. */
const FeatureGate: React.FC<{ feature: FeatureKey; children: React.ReactNode }> = ({ feature, children }) => {
  const flags = useFeatureFlags();
  if (!flags[feature]) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider>
        <FeaturesProvider>
        <Router>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: '100vh',
            }}
          >
            <Header />
            <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Navigate to="/login" replace />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                {/* Public, token-gated in-person registration (scanned QR). */}
                <Route path="/join" element={<JoinPage />} />

                {/* Protected Routes */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <ProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile/edit"
                  element={
                    <ProtectedRoute>
                      <EditProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminDashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/check-in"
                  element={
                    <ProtectedRoute>
                      <CheckInManagementPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/registration-qr"
                  element={
                    <ProtectedRoute>
                      <RegistrationQRPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/kids-club"
                  element={
                    <ProtectedRoute>
                      <FeatureGate feature="kidsClub"><KidsClubBookingPage /></FeatureGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/kids-club/staff"
                  element={
                    <ProtectedRoute>
                      <FeatureGate feature="kidsClub"><KidsClubStaffPage /></FeatureGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/kids-club/admin"
                  element={
                    <ProtectedRoute>
                      <FeatureGate feature="kidsClub"><KidsClubAdminPage /></FeatureGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/non-member-management"
                  element={
                    <ProtectedRoute>
                      <NonMemberManagementPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/non-members/:id"
                  element={
                    <ProtectedRoute>
                      <NonMemberDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/members/:id"
                  element={
                    <ProtectedRoute>
                      <MemberDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/conversion-applications/:id"
                  element={
                    <ProtectedRoute>
                      <ConversionApplicationPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/courts"
                  element={
                    <ProtectedRoute>
                      <FeatureGate feature="tennis"><CourtBookingPage /></FeatureGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/pro-management"
                  element={
                    <ProtectedRoute>
                      <FeatureGate feature="tennis"><ProManagementPage scope="tennis" /></FeatureGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/tennis-management"
                  element={
                    <ProtectedRoute>
                      <FeatureGate feature="tennis"><ProManagementPage scope="tennis" /></FeatureGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/swim-management"
                  element={
                    <ProtectedRoute>
                      <FeatureGate feature="swim"><ProManagementPage scope="swim" /></FeatureGate>
                    </ProtectedRoute>
                  }
                />

                {/* Swim module */}
                <Route
                  path="/swim"
                  element={
                    <ProtectedRoute>
                      <FeatureGate feature="swim"><SwimClassesPage /></FeatureGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/swim/my"
                  element={
                    <ProtectedRoute>
                      <FeatureGate feature="swim"><MyEnrollmentsPage /></FeatureGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/swim/pending"
                  element={
                    <ProtectedRoute>
                      <FeatureGate feature="swim"><PendingEnrollmentsAdminPage /></FeatureGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/swim/cancellations"
                  element={
                    <ProtectedRoute>
                      <FeatureGate feature="swim"><ProgramCancellationsPage /></FeatureGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/swim/class-levels"
                  element={
                    <ProtectedRoute>
                      <FeatureGate feature="swim"><ClassLevelsAdminPage /></FeatureGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/swim/leagues"
                  element={
                    <ProtectedRoute>
                      <FeatureGate feature="swim"><SwimLeaguesAdminPage /></FeatureGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/coach/program/:programId/roster"
                  element={
                    <ProtectedRoute>
                      <FeatureGate feature="swim"><CoachRosterPage /></FeatureGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/programs/enrollments"
                  element={
                    <ProtectedRoute>
                      <AdminProgramEnrollmentsPage scope="all" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/swim/enrollments"
                  element={
                    <ProtectedRoute>
                      <FeatureGate feature="swim"><AdminProgramEnrollmentsPage scope="swim" /></FeatureGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/tennis/enrollments"
                  element={
                    <ProtectedRoute>
                      <FeatureGate feature="tennis"><AdminProgramEnrollmentsPage scope="tennis" /></FeatureGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/swim/programs"
                  element={
                    <ProtectedRoute>
                      <FeatureGate feature="swim"><SwimProgramsAdminPage /></FeatureGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/swim/schedule"
                  element={
                    <ProtectedRoute>
                      <FeatureGate feature="swim"><MyScheduleParentPage /></FeatureGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/coach/schedule"
                  element={
                    <ProtectedRoute>
                      <FeatureGate feature="swim"><CoachSchedulePage /></FeatureGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/feature-flags"
                  element={
                    <ProtectedRoute>
                      <FeatureFlagsAdminPage />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Box>
            <Footer />
          </Box>
        </Router>
        </FeaturesProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
